import { getUserImageUrl } from '../constants/config';
import { extractEntity, extractList } from './tournamentsApi';

export const USERS_PAGE_SIZE = 40;
export const ROLE_GUEST = 1;
export const ROLE_PLAYER = 2;
export const ROLE_CAPTAIN = 3;
export const ROLE_VICE_CAP = 4;
export const ROLE_ROOT = 7;
export const ADMIN_ROLE_MIN = 5;
export const ADMIN_ROLE_MAX = 13;

export const USER_ROLES = [
  { id: 1, name: 'guest', labelKey: 'roleGuest' },
  { id: 2, name: 'player', labelKey: 'rolePlayer' },
  { id: 3, name: 'captain', labelKey: 'roleCaptain' },
  { id: 4, name: 'vice_cap', labelKey: 'roleViceCap' },
  { id: 5, name: 'administrator', labelKey: 'roleAdministrator' },
  { id: 6, name: 'head', labelKey: 'roleHead' },
  { id: 7, name: 'root', labelKey: 'roleRoot' },
  { id: 8, name: 'operator', labelKey: 'roleOperator' },
  { id: 9, name: 'journalist', labelKey: 'roleJournalist' },
  { id: 10, name: 'photographer', labelKey: 'rolePhotographer' },
  { id: 11, name: 'doctor', labelKey: 'roleDoctor' },
  { id: 12, name: 'inspector', labelKey: 'roleInspector' },
  { id: 13, name: 'referee', labelKey: 'roleReferee' },
];

export const isAdminRole = (roleId) => {
  const id = Number(roleId);
  return id >= ADMIN_ROLE_MIN && id <= ADMIN_ROLE_MAX;
};

export const isCaptainRole = (roleId) => {
  const id = Number(roleId);
  return id === ROLE_CAPTAIN || id === ROLE_VICE_CAP;
};

export const ASSIGNABLE_USER_ROLES = USER_ROLES.filter((role) => (
  isAdminRole(role.id) || isCaptainRole(role.id)
));

/** Селект ролей: только id из login.admin_access.users.assignable_roles.
 *  guest(1) / player(2) не назначаются вручную — сервер выводит их сам. */
export const filterAssignableUserRoles = (assignableIds) => {
  const set = new Set((assignableIds || []).map(Number).filter((id) => id > 0));
  const base = set.size
    ? USER_ROLES.filter((role) => set.has(role.id))
    : ASSIGNABLE_USER_ROLES;
  return base.filter((role) => !isGuestRole(role.id) && !isPlayerRole(role.id));
};

export const isGuestRole = (roleId) => Number(roleId) === ROLE_GUEST;
export const isPlayerRole = (roleId) => Number(roleId) === ROLE_PLAYER;

const ROLE_BY_ID = USER_ROLES.reduce((acc, role) => {
  acc[role.id] = role;
  return acc;
}, {});

const ROLE_BY_NAME = USER_ROLES.reduce((acc, role) => {
  acc[role.name] = role;
  return acc;
}, {});

export const USER_INFO_FIELDS = [
  { key: 'id', labelKey: 'usrId' },
  { key: 'login', labelKey: 'usrLogin' },
  { key: 'identificator', labelKey: 'usrIdentificator' },
  { key: 'name', labelKey: 'usrName' },
  { key: 'family', labelKey: 'usrFamily' },
  { key: 'father_name', labelKey: 'usrFatherName' },
  { key: 'email', labelKey: 'usrEmail' },
  { key: 'phone', labelKey: 'usrPhone' },
  { key: 'birthday', labelKey: 'usrBirthday' },
  { key: 'vk', labelKey: 'usrVk' },
  { key: 'telegramId', labelKey: 'usrTelegram', aliases: ['telegram_id', 'telegram'] },
  { key: 'city_name', labelKey: 'usrCity' },
  { key: 'city_id', labelKey: 'usrCityId' },
  { key: 'confirmed', labelKey: 'usrConfirmed', type: 'bool' },
  { key: 'default_champ_id', labelKey: 'usrDefaultChamp' },
  { key: 'role', labelKey: 'usrMainRole', type: 'role' },
];

const SKIP_USER_KEYS = new Set([
  'password',
  'refresh_token',
  'roles',
  'championships',
  'players',
  'player_id',
  'admin_access',
  'champ_access',
  'champs_ids',
  'unrestricted_champs',
  'image',
  'access_token',
  'data',
]);

USER_INFO_FIELDS.forEach((field) => {
  SKIP_USER_KEYS.add(field.key);
  (field.aliases || []).forEach((alias) => SKIP_USER_KEYS.add(alias));
});

const toNumber = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const parseRoleId = (value) => {
  if (value == null || value === '') return null;
  if (typeof value === 'object') {
    return parseRoleId(value.id ?? value.role ?? value.role_id ?? value.level);
  }
  if (typeof value === 'string' && value && !/^\d+$/.test(value.trim())) {
    const found = ROLE_BY_NAME[value.trim()];
    return found ? found.id : toNumber(value);
  }
  return toNumber(value);
};

export const getUserPhotoUrl = (image) => getUserImageUrl(image);

export const userCardPath = (userId) => `/users/${userId}`;
export const userNewPath = () => '/users/new';

export const userDisplayName = (user) => (
  [user?.family, user?.name, user?.father_name].filter(Boolean).join(' ').trim()
);

export const roleById = (roleId) => ROLE_BY_ID[Number(roleId)] || null;

export const roleLabel = (t, roleId) => {
  const role = roleById(roleId);
  return role ? t(role.labelKey) : `#${roleId}`;
};

export const championshipIdFrom = (item) => toNumber(
  item?.championship_id ?? item?.championat_id ?? item?.champ_id
  ?? item?.championship?.id ?? item?.championat?.id
);

export const teamIdFrom = (item) => {
  if (item == null || typeof item !== 'object') return toNumber(item);
  if (item.team && typeof item.team === 'object') {
    return toNumber(item.team.id ?? item.team.team_id);
  }
  return toNumber(item.team_id);
};

const textValue = (value) => {
  if (value == null) return null;
  const text = String(value).trim();
  return text ? text : null;
};

export const teamNameFrom = (item) => {
  if (item == null || typeof item !== 'object') return null;
  const nested = item.team && typeof item.team === 'object' ? item.team : null;
  return textValue(
    item.team_name
    ?? item.teamName
    ?? nested?.name
    ?? nested?.team_name
    ?? (typeof item.team === 'string' ? item.team : null)
  );
};

export const championshipNameFrom = (item) => {
  if (item == null || typeof item !== 'object') return null;
  return textValue(
    item.championship_name
    ?? item.championat_name
    ?? item.champ_name
    ?? item.championship?.name
    ?? item.championat?.name
  );
};

export const normalizeRoleAssignment = (item) => {
  if (item == null || item === '') return null;
  if (typeof item !== 'object') {
    const role = parseRoleId(item);
    return role ? { role } : null;
  }
  const role = parseRoleId(item.role ?? item.id ?? item.role_id ?? item.level);
  if (!role) return null;
  const championship_id = championshipIdFrom(item);
  const championship_name = championshipNameFrom(item);
  const team_id = teamIdFrom(item);
  const team_name = teamNameFrom(item);
  const assignment = { role };
  if (championship_id) assignment.championship_id = championship_id;
  if (championship_name) assignment.championship_name = championship_name;
  if (team_id) assignment.team_id = team_id;
  if (team_name) assignment.team_name = team_name;
  return assignment;
};

export const normalizeRoleAssignments = (value) => {
  const list = Array.isArray(value) ? value : (value != null && value !== '' ? [value] : []);
  return list.map(normalizeRoleAssignment).filter(Boolean);
};

export const roleAssignmentKey = (item) => {
  if (!item) return '';
  if (item.team_id) return `${item.role}:t${item.team_id}`;
  if (item.championship_id) return `${item.role}:c${item.championship_id}`;
  return `${item.role}`;
};

export const assignmentLabel = (t, item) => {
  const role = roleLabel(t, item?.role);
  const team = item?.team_name || teamNameFrom(item);
  const champ = item?.championship_name || championshipNameFrom(item);
  if (team) return champ ? `${role} · ${team} · ${champ}` : `${role} · ${team}`;
  if (champ) return `${role} · ${champ}`;
  return role;
};

export const serializeRoleAssignments = (assignments) => (
  (assignments || []).map((item) => {
    const role = Number(item.role);
    if (!Number.isFinite(role) || role <= 0) return null;
    // guest / player не пишем — их выставляет сервер по привязке игрока
    if (role === ROLE_GUEST || role === ROLE_PLAYER) return null;
    const payload = { role };
    if (isCaptainRole(role)) {
      if (!item.team_id) return null;
      payload.team_id = Number(item.team_id);
      if (item.championship_id) payload.championship_id = Number(item.championship_id);
      return payload;
    }
    if (isAdminRole(role)) {
      // root — глобальная роль без чемпионата
      if (role === ROLE_ROOT) return payload;
      if (!item.championship_id) return null;
      payload.championship_id = Number(item.championship_id);
      return payload;
    }
    return null;
  }).filter(Boolean)
);

export const hasRootAssignment = (assignments) => (
  (assignments || []).some((item) => Number(item.role) === ROLE_ROOT)
);

export const sameAssignments = (left, right) => {
  if ((left || []).length !== (right || []).length) return false;
  const rightSet = new Set((right || []).map(roleAssignmentKey));
  return (left || []).every((item) => rightSet.has(roleAssignmentKey(item)));
};

export const normalizeRoleIds = (value) => {
  const list = Array.isArray(value) ? value : (value != null && value !== '' ? [value] : []);
  const ids = list.map(parseRoleId).filter((id) => id != null && id > 0);
  return [...new Set(ids)];
};

export const championshipIds = (value) => {
  const list = Array.isArray(value) ? value : [];
  return [...new Set(list.map((item) => {
    if (item && typeof item === 'object') return championshipIdFrom(item) ?? toNumber(item.id);
    return toNumber(item);
  }).filter((id) => id != null && id > 0))];
};

export const extractUsersPage = (data) => {
  if (Array.isArray(data)) {
    return {
      items: data.map((item) => ({ ...item, roles: normalizeRoleAssignments(item?.roles) })),
      total: null,
    };
  }
  const items = Array.isArray(data?.users) ? data.users : extractList(data);
  const totalRaw = data?.total ?? data?.count ?? data?.totalCount ?? data?.total_count;
  const total = Number(totalRaw);
  return {
    items: items.map((item) => ({ ...item, roles: normalizeRoleAssignments(item?.roles) })),
    total: Number.isFinite(total) && total >= 0 ? total : null,
  };
};

export const extractUser = (data) => {
  const entity = extractEntity(data);
  return {
    ...entity,
    id: toNumber(entity.id ?? entity.user_id),
    roles: normalizeRoleAssignments(entity.roles),
    roles_scoped: Boolean(entity.roles_scoped),
    players: extractList(entity.players),
    player_id: extractList(entity.player_id).map(toNumber).filter((id) => id != null),
  };
};

const readFieldValue = (user, field) => {
  if (user?.[field.key] != null && user[field.key] !== '') return user[field.key];
  const alias = (field.aliases || []).find((key) => user?.[key] != null && user[key] !== '');
  return alias ? user[alias] : user?.[field.key];
};

const formatScalar = (value) => {
  if (value == null || value === '') return '';
  if (typeof value === 'boolean') return value;
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
};

export const formatUserInfoFields = (user, t) => {
  const fields = USER_INFO_FIELDS.map((field) => {
    const raw = readFieldValue(user, field);
    let value = '—';
    if (field.type === 'bool') {
      if (raw == null || raw === '') value = '—';
      else value = Number(raw) === 1 || raw === true ? t('usrYes') : t('usrNo');
    } else if (field.type === 'role') {
      value = raw == null || raw === '' ? '—' : roleLabel(t, raw);
    } else if (raw != null && raw !== '') {
      value = formatScalar(raw) || '—';
    }
    return { key: field.key, label: t(field.labelKey), value };
  });

  const extra = Object.keys(user || {})
    .filter((key) => !SKIP_USER_KEYS.has(key))
    .map((key) => {
      const raw = user[key];
      if (raw == null || raw === '') return null;
      if (typeof raw === 'object') return null;
      return { key, label: key, value: String(raw) };
    })
    .filter(Boolean);

  return fields.concat(extra);
};

export const sameIdSet = (left, right) => {
  if (left.length !== right.length) return false;
  const rightSet = new Set(right.map(Number));
  return left.every((id) => rightSet.has(Number(id)));
};
